import {
    getLocation,
    LOCATION_CHANGE,
    push,
    replace,
} from 'connected-react-router';
import { stringify, parse } from 'query-string';
import _ from 'lodash';
import companyStatsService from '../../services/companyStatsService';
import { setLoading, setErrorMessage } from './ui';
import { logout } from './user';
import { getDefaultFrom, getDefaultTo, getGroupingPeriod } from '../storeUtils/dateUtils';
import routeConstants from '../../Routes/routeConstants';
import { isUnauthorized } from '../../services/utils/responseValidator';

// ACTIONS
const CLEAR_COMPANY_DATA = 'CLEAR_COMPANY_DATA';
const CLEAR_COMPANY_FORM = 'CLEAR_COMPANY_FORM';
const GROUP_COMPANY_DATA_BY = 'GROUP_COMPANY_DATA_BY';
const SET_COMPANY_FORM_STATE = 'SET_COMPANY_FORM_STATE';
const SET_COMPANY_FORM_FIELD = 'SET_COMPANY_FORM_FIELD';
const SET_FORMATED_COMPANY_DATA = 'SET_FORMATED_COMPANY_DATA';
const SET_COMPANY_SELECTED_SUGGESTIONS = 'SET_COMPANY_SELECTED_SUGGESTIONS';
const SET_COMPANY_LOCATION = 'SET_COMPANY_LOCATION';
const SET_SELECTED_COMPANY_LEADS_GRAPH_KEYS = 'SET_SELECTED_COMPANY_LEADS_GRAPH_KEYS';
const SET_SELECTED_COMPANY_CLICKS_GRAPH_KEYS = 'SET_SELECTED_COMPANY_CLICK_GRAPH_KEYS';
const SET_SELECTED_COMPANY_IMPRESSION_GRAPH_KEYS = 'SET_SELECTED_COMPANY_IMPRESSION_GRAPH_KEYS';

const initialState = {
    formState: {
        hid: '',
        orgNo: '',
        from: getDefaultFrom(),
        to: getDefaultTo(),
    },
    groupBy: 'MONTH',
    selectedSuggestions: [],
    location: {
        pathname: routeConstants.COMPANY_STATS,
        hash: '',
        search: '',
    },
    stats: {},
    selectedGraphKeysLead: null,
    selectedGraphKeysClick: null,
    selectedGraphKeysImpression: null,
};

// REDUCER
const reducer = (state = initialState, action) => {
    switch (action.type) {
    case SET_FORMATED_COMPANY_DATA: {
        const clone = _.cloneDeep(action.value);
        const {
            graphDataPoint,
            graphDate,
        } = action.value;
        const {
            click,
            lead,
            impression,
        } = graphDataPoint;

        return Object.assign({}, state, {
            stats: clone,
            graphDataPointClick: click[state.groupBy.toLowerCase()],
            graphDataPointImpression: impression[state.groupBy.toLowerCase()],
            graphDataPointLead: lead[state.groupBy.toLowerCase()],
            graphDate: graphDate[state.groupBy.toLowerCase()],
        });
    }
    case GROUP_COMPANY_DATA_BY: {
        const {
            graphDataPoint,
            graphDate,
        } = state.stats;
        const {
            click,
            lead,
            impression,
        } = graphDataPoint || {};

        return Object.assign({}, state, {
            groupBy: action.value,
            graphDataPointClick: click && click[action.value.toLowerCase()],
            graphDataPointImpression: impression && impression[action.value.toLowerCase()],
            graphDataPointLead: lead && lead[action.value.toLowerCase()],
            graphDate: graphDate && graphDate[action.value.toLowerCase()],
        });
    }
    case SET_COMPANY_FORM_STATE: {
        return Object.assign({}, state, {
            formState: action.value,
        });
    }
    case SET_COMPANY_FORM_FIELD: {
        return Object.assign({}, state, {
            formState: { ...state.formState, ...action.value },
        });
    }
    case SET_COMPANY_LOCATION: {
        return Object.assign({}, state, {
            location: action.value,
        });
    }
    case SET_COMPANY_SELECTED_SUGGESTIONS: {
        return Object.assign({}, state, {
            selectedSuggestions: action.value,
        });
    }
    case CLEAR_COMPANY_DATA: {
        return Object.assign({}, state, {
            stats: {},
            graphDataPointClick: null,
            graphDataPointImpression: null,
            graphDataPointLead: null,
            graphDate: null,
            selectedGraphKeysLead: null,
            selectedGraphKeysClick: null,
            selectedGraphKeysImpression: null,
        });
    }
    case CLEAR_COMPANY_FORM: {
        return Object.assign({}, state, {
            formState: initialState.formState,
            selectedSuggestions: [],
        });
    }
    case SET_SELECTED_COMPANY_LEADS_GRAPH_KEYS: {
        return Object.assign({}, state, {
            selectedGraphKeysLead: action.value,
        });
    }
    case SET_SELECTED_COMPANY_CLICKS_GRAPH_KEYS: {
        return Object.assign({}, state, {
            selectedGraphKeysClick: action.value,
        });
    }
    case SET_SELECTED_COMPANY_IMPRESSION_GRAPH_KEYS: {
        return Object.assign({}, state, {
            selectedGraphKeysImpression: action.value,
        });
    }
    case LOCATION_CHANGE: {
        const { pathname, state: actionPayloadState } = action.payload.location || {};
        const isCompanyStatsLocation = pathname === routeConstants.COMPANY_STATS;

        if (isCompanyStatsLocation) {
            const { companystats } = actionPayloadState || {};
            const {
                formState,
                groupBy,
                stats,
                selectedGraphKeysLead,
                selectedGraphKeysClick,
                selectedGraphKeysImpression,
                selectedSuggestions,
            } = companystats || initialState;

            const {
                graphDataPoint,
                graphDate,
            } = stats;

            const {
                click,
                lead,
                impression,
            } = graphDataPoint || {};

            return Object.assign({}, state, {
                graphDataPointClick: click && click[groupBy.toLowerCase()],
                graphDataPointImpression: impression && impression[groupBy.toLowerCase()],
                graphDataPointLead: lead && lead[groupBy.toLowerCase()],
                graphDate: graphDate && graphDate[groupBy.toLowerCase()],
                selectedGraphKeysLead,
                selectedGraphKeysClick,
                selectedGraphKeysImpression,
                location: action.payload.location,
                formState,
                groupBy,
                stats,
                selectedSuggestions,
            });
        }
        return state;
    }
    default:
        return state;
    }
};

// ACTION CREATORS
const clearData = () => ({
    type: CLEAR_COMPANY_DATA,
});
const clearForm = () => ({
    type: CLEAR_COMPANY_FORM,
});

const groupStatsBy = groupBy => ({
    type: GROUP_COMPANY_DATA_BY,
    value: groupBy,
});

const internalSetFormattedData = stats => ({
    type: SET_FORMATED_COMPANY_DATA,
    value: stats,
});

const internalSetFormState = formData => ({
    type: SET_COMPANY_FORM_STATE,
    value: formData,
});

const setFormField = field => ({
    type: SET_COMPANY_FORM_FIELD,
    value: field,
});

const setSelectedCompanySuggestions = selected => ({
    type: SET_COMPANY_SELECTED_SUGGESTIONS,
    value: selected,
});

const internalSetCompanyLocation = location => ({
    type: SET_COMPANY_LOCATION,
    value: location,
});

const setSelectedLeadsGraphKeys = selected => ({
    type: SET_SELECTED_COMPANY_LEADS_GRAPH_KEYS,
    value: selected,
});

const setSelectedClicksGraphKeys = selected => ({
    type: SET_SELECTED_COMPANY_CLICKS_GRAPH_KEYS,
    value: selected,
});

const setSelectedImpressionsGraphKeys = selected => ({
    type: SET_SELECTED_COMPANY_IMPRESSION_GRAPH_KEYS,
    value: selected,
});

// THUNK
const clearAll = () => (dispatch, getState) => {
    dispatch(clearData());
    dispatch(clearForm());
    const { formState } = getState().companystats;
    dispatch(push({
        pathname: routeConstants.COMPANY_STATS,
        search: stringify(formState),
        state: getState(),
    }));
};

const getCompanyStats = formData => (dispatch, getState) => {
    dispatch(setLoading(true));
    dispatch(clearData());
    dispatch(internalSetFormState(formData));
    companyStatsService.getCompanyStats(formData)
        .then((apiResonse) => {
            dispatch(groupStatsBy(getGroupingPeriod(formData.from, formData.to)));
            dispatch(internalSetFormattedData(apiResonse.response));
            dispatch(push({
                pathname: routeConstants.COMPANY_STATS,
                search: stringify(formData),
                state: getState(),
            }));
            dispatch(internalSetCompanyLocation(getLocation(getState())));
            dispatch(setLoading(false));
        })
        .catch((err) => {
            dispatch(setLoading(false));
            if (isUnauthorized(err.response)) {
                dispatch(logout());
            }
            dispatch(setErrorMessage(err.message));
        });
};

const setInitialCompanyFormState = location => (dispatch, getState) => {
    const { formState } = getState().companystats;
    const formParams = parse(location.search);

    const newFormState = {
        hid: formParams.hid || formState.hid,
        orgNo: formParams.orgNo || formState.orgNo,
        to: formParams.to || formState.to,
        from: formParams.from || formState.from,
    };

    dispatch(internalSetFormState(newFormState));

    dispatch(replace({
        pathname: routeConstants.COMPANY_STATS,
        search: stringify(newFormState),
        state: getState(),
    }));

    if (!!newFormState.hid || !!newFormState.orgNo) {
        dispatch(getCompanyStats(newFormState));
    }
};

export {
    getCompanyStats,
    setSelectedCompanySuggestions,
    groupStatsBy,
    setFormField,
    clearForm,
    clearData,
    clearAll,
    setInitialCompanyFormState,
    setSelectedLeadsGraphKeys,
    setSelectedClicksGraphKeys,
    setSelectedImpressionsGraphKeys,
};

export default reducer;
